import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message, Modal } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import authActions from '../actions/authActions';


const SessionTimer: React.FC = () => {

  const dispatch = useDispatch();
  const { authData } = useSelector((state: any) => state.otpverifyReducer);
  const renewTokenData = useSelector((state: any) => state.renewTokenReducer);
  const navigate = useNavigate();

  const [warnVisible, setWarnVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number>(60);


  const IDLE_MS = 200 * 60 * 1000;
  const PROMPT_BEFORE_IDLE_MS = 500; 
  const isIdleRef = useRef<boolean>(false);

  const logoutTimerRef = useRef<number | null>(null);
  const warnTimerRef = useRef<number | null>(null);
  const warnCountdownRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const promptBeforeIdleTimerRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isRenewingRef = useRef<boolean>(false);
  const warnVisibleRef = useRef<boolean>(false);
  const renewLoadingRef = useRef<boolean>(false);

  const clearIdleTimers = () => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (promptBeforeIdleTimerRef.current) {
      window.clearTimeout(promptBeforeIdleTimerRef.current);
      promptBeforeIdleTimerRef.current = null;
    }
  };

  const scheduleIdleTimers = useCallback(() => {
    clearIdleTimers();

    promptBeforeIdleTimerRef.current = window.setTimeout(() => {
      const idleForMs = Date.now() - lastActivityRef.current;
      const aboutToBeIdle = idleForMs >= (IDLE_MS - PROMPT_BEFORE_IDLE_MS);
      if (!aboutToBeIdle) return;

      isIdleRef.current = true;
      setWarnVisible(true);
    }, Math.max(0, IDLE_MS - PROMPT_BEFORE_IDLE_MS));

    idleTimerRef.current = window.setTimeout(() => {
      const idleForMs = Date.now() - lastActivityRef.current;
      if (idleForMs < IDLE_MS) return;
      isIdleRef.current = true;
    }, IDLE_MS);
  }, [IDLE_MS]);

  useEffect(() => {

    isIdleRef.current = false;
    scheduleIdleTimers();

    if (logoutTimerRef.current) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (warnTimerRef.current) {
      window.clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (warnCountdownRef.current) {
      window.clearInterval(warnCountdownRef.current);
      warnCountdownRef.current = null;
    }
    setWarnVisible(false);


    let effectiveAuth: any = authData;

      try {
        const stored = localStorage.getItem('AUTHDATA');
        effectiveAuth = stored ? JSON.parse(stored) : null;
      } catch {
        effectiveAuth = null;
      }

    
    if (!effectiveAuth) {
      return; 
    }

    const now = Date.now();

    const getExpiryMs = () => {
      const expiresAtMs: number | undefined = (() => {
        if (typeof effectiveAuth?.expiresAt === 'number') return effectiveAuth.expiresAt;
        if (typeof effectiveAuth?.tokenExpiry === 'number') return effectiveAuth.tokenExpiry;
        if (typeof effectiveAuth?.exp === 'number') return effectiveAuth.exp * 1000; 
        if (typeof effectiveAuth?.expiresIn === 'number') {
          const base = typeof effectiveAuth?.issuedAt === 'number' ? effectiveAuth.issuedAt : now;
          return base + effectiveAuth.expiresIn * 1000;
        }
        return undefined;
      })();
      return typeof expiresAtMs === 'number' ? expiresAtMs : now + 30 * 60 * 1000; 
    };

    const expiryMs = getExpiryMs();
    const msUntilExpiry = Math.max(0, expiryMs - now);
    const msUntilWarn = Math.max(0, msUntilExpiry - 4 * 60 * 1000);

    warnTimerRef.current = window.setTimeout(() => {
      const idleForMs = Date.now() - lastActivityRef.current;
      const isIdle = idleForMs >= 10 * 1000;
      if (!isIdle) return; 
      setWarnVisible(true);
      const initial = Math.ceil(Math.max(0, (expiryMs - Date.now())) / 1000);
      setSecondsLeft(initial);
      if (warnCountdownRef.current) {
        window.clearInterval(warnCountdownRef.current);
        warnCountdownRef.current = null;
      }
      warnCountdownRef.current = window.setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            if (warnCountdownRef.current) {
              window.clearInterval(warnCountdownRef.current);
              warnCountdownRef.current = null;
            }
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }, msUntilWarn);

    return () => {
      clearIdleTimers();
      if (logoutTimerRef.current) {
        window.clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      if (warnTimerRef.current) {
        window.clearTimeout(warnTimerRef.current);
        warnTimerRef.current = null;
      }
      if (warnCountdownRef.current) {
        window.clearInterval(warnCountdownRef.current);
        warnCountdownRef.current = null;
      }
    };
  }, [authData, scheduleIdleTimers]);

  useEffect(() => {
    const activityEvents = ['click', 'keydown', 'mousemove', 'pointerdown', 'pointermove'];  
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      isIdleRef.current = false;
      scheduleIdleTimers();
      if (warnVisible) setWarnVisible(false);

      try {
        const stored =  localStorage.getItem('AUTHDATA');
        if (!stored) return;
        const auth = JSON.parse(stored);
        const now = Date.now();
        const expiry = (typeof auth?.tokenExpiry === 'number') ? auth.tokenExpiry 
        : (typeof auth?.exp === 'number') ? auth.exp * 1000 : undefined;
        if (!expiry) return;
        const delta = expiry - now; 
        const thresholdMs = 30 * 1000;    
          
        if (delta <= thresholdMs && !renewLoadingRef.current && !isRenewingRef.current && !warnVisibleRef.current ) {
          const phone = auth?.mobileNumber ?? '';
          isRenewingRef.current = true; 
          dispatch(authActions.renewtokenRequest({ phoneNumber: phone }));
        }
      } catch {}
    };

    activityEvents.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
    return () => {
      clearIdleTimers();
      activityEvents.forEach((evt) => window.removeEventListener(evt, handleActivity));
    };
  }, [scheduleIdleTimers, dispatch, warnVisible]);

  useEffect(() => {
    warnVisibleRef.current = warnVisible;
  }, [warnVisible]);
  useEffect(() => {
    renewLoadingRef.current = Boolean(renewTokenData?.isLoading);
  }, [renewTokenData]);


 useEffect(() => {
  if (renewTokenData?.isSuccess) {
    try {
      const stored = localStorage.getItem('AUTHDATA');
      if (!stored) {
        return;
      }
      const current = JSON.parse(stored);
      const updated = {
        ...current,
        authToken: renewTokenData?.renewData?.authToken,
        tokenExpiry: renewTokenData?.renewData?.tokenExpiry,
      };
      localStorage.setItem('AUTHDATA', JSON.stringify(updated));
    } catch (e) {} finally {
      isRenewingRef.current = false;
    }
  } else if (renewTokenData?.isError) {
    isRenewingRef.current = false;
  }
}, [renewTokenData]);


  const handleRelogin = () => {
    try { localStorage.removeItem('AUTHDATA'); } catch {}
    setWarnVisible(false);
    navigate('/login');
  };

  return (
    <>
      <Modal title="Session Expiring" open={warnVisible} onOk={handleRelogin} 
        okText="Log-in" centered>
        <p>You may re-login now to continue.</p>
      </Modal>
    </>
  );
};

export default SessionTimer;
